import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bell, ExternalLink } from "lucide-react";
import api from "@/lib/api";

// Poll cadence — mirrors Overview refresh so we never double-tax the backend.
const POLL_MS = 30000;

function fmtRel(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function StatusDot({ status }) {
  const tone = status === "alert"
    ? "bg-[#ef5757]"
    : status === "idle"
    ? "bg-[#c9ccd2]"
    : "bg-[#2dd4a7]";
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone}`} />;
}

/**
 * NotificationBell — Phase 0 in-app notifications feed backed purely by
 * agent_runs. Bell icon + unread count badge + hover/click popover with the
 * latest 8 runs (respecting user's watchlist). Marks-as-read on open.
 * The full feed lives at `/{slug}/app/notifications`.
 */
export default function NotificationBell() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/agent_runs?limit=8");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // Silent fail — bell stays quiet when API is down, matches Overview strip pattern
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  // Outside-click closes the popover
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openPopover = async () => {
    setOpen((o) => !o);
    // Optimistic: clear badge locally, and mark server-side.
    if (unread > 0) {
      const zero = 0;
      setUnread(zero);
      try {
        await api.post("/notifications/mark-read", {});
      } catch {
        // If the mark-read fails, next poll will re-populate the badge
      }
    }
  };

  const goToFull = () => {
    setOpen(false);
    if (slug) nav(`/${slug}/app/notifications`);
    else nav("/admin/notifications");
  };

  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div className="relative" data-testid="notification-bell-wrap">
      <button
        ref={btnRef}
        onClick={openPopover}
        aria-label={`Notifications (${unread} unread)`}
        data-testid="notification-bell-btn"
        className="relative w-full flex items-center justify-center gap-2 text-[12.5px] font-semibold py-2 rounded-md border border-[color:var(--cc-navy-border)] hover:bg-[color:var(--cc-navy-card)] transition-colors"
      >
        <Bell size={14} strokeWidth={1.8} />
        <span>Notifications</span>
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ef5757] text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="notification-popover"
          className="fixed lg:absolute lg:bottom-full lg:mb-2 lg:left-0 lg:right-0 lg:w-[340px] bottom-4 left-4 right-4 max-w-[92vw] lg:max-w-none bg-white border border-[#e7e9ee] rounded-xl shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#eef1f6] flex items-center justify-between">
            <div className="font-display font-bold text-[13px] text-[#0e1420]">Recent activity</div>
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
              {items.length ? `${items.length} shown` : "—"}
            </div>
          </div>

          <div className="max-h-[380px] overflow-y-auto divide-y divide-[#eef1f6]">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-[color:var(--cc-ink-muted)]">
                Nothing yet. Agent runs will show up here as they happen.
              </div>
            ) : items.map((it) => (
              <div key={it.id} className="px-4 py-2.5 hover:bg-[#fafbfc] transition-colors" data-testid={`notif-item-${it.id}`}>
                <div className="flex items-start gap-2.5">
                  <div className="pt-1"><StatusDot status={it.status} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[12.5px] font-semibold text-[#0e1420] truncate">
                        {it.agent_name || it.agent_id}
                      </div>
                      <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] font-mono-data whitespace-nowrap">
                        {fmtRel(it.started_at)}
                      </div>
                    </div>
                    {it.summary && (
                      <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5 line-clamp-2">
                        {it.summary}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={goToFull}
            data-testid="notification-see-all"
            className="w-full px-4 py-2.5 text-[12.5px] font-semibold text-[#0072bc] hover:bg-[#e7f4fb] border-t border-[#eef1f6] transition-colors flex items-center justify-center gap-1.5"
          >
            See all <ExternalLink size={12} strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
}
